import { Flex, Loader } from "@fluentui/react-northstar";
import React, { useState, useEffect } from "react";
import i18n from "../helper/i18n";
import { Subscription } from "../components/Subscription/Subscription";

const AiStudio = ({ type, user, url, config }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(Date.now());

  // Handle page refresh events
  useEffect(() => {
    // Check if this is a page refresh (F5)
    const isPageRefresh =
      window.performance &&
      window.performance.navigation &&
      window.performance.navigation.type === 1;

    if (isPageRefresh) {
      // Generate a new key to force iframe remount on refresh
      setIframeKey(Date.now());
    }

    // Add event listener for beforeunload to handle refresh
    const handleBeforeUnload = () => {
      // This will run right before the page refreshes
      localStorage.setItem("aiStudio_refreshing", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Check if we're coming back from a refresh
    const isRefreshing = localStorage.getItem("aiStudio_refreshing") === "true";
    if (isRefreshing) {
      // Clear the flag
      localStorage.removeItem("aiStudio_refreshing");
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
  const getCurrentLanguage = () => {
    const languageMappings = {
      en: "en",
      "en-GB": "en",
      "en-US": "en",
      es: "es",
      pt: "pt",
      de: "de",
      fr: "fr",
      hi: "hi",
    };
    return languageMappings[i18n.language];
  };

  const currentLanguage = getCurrentLanguage();

  // Use provided config or fallback to default
  const defaultConfig = {
    className: `ai-${type}-app`,
    source: `ai-${type}`,
    url: url,
    additionalParams: {
      slug: user.slug,
      email: user.mail,
      language: currentLanguage,
    },
  };

  const currentConfig = {
    ...defaultConfig,
    ...config,
    additionalParams: {
      ...defaultConfig.additionalParams,
      ...config,
      language: currentLanguage, // Always use current language
    },
  };

  const postCrossDomainMessage = () => {
    const iframeEl = document.getElementsByClassName(currentConfig.className);
    const userData = {
      accessToken: user.accessToken,
      email: user.mail,
      role: user.actualRole,
    };

    iframeEl[0]?.contentWindow?.postMessage(
      {
        source: currentConfig.source,
        message: { user: userData, isFromACV: true },
      },
      currentConfig.url
    );
  };

  const getIframeUrl = () => {
    const baseUrl = currentConfig.url.split("?")[0];
    // const baseUrl = "http://localhost:3001";

    const flatParams = {};

    for (const [key, value] of Object.entries(currentConfig.additionalParams)) {
      if (
        key === "slug" &&
        typeof value === "string" &&
        value.startsWith("?slug=")
      ) {
        flatParams["slug"] = value.replace("?slug=", "");
      } else if (typeof value === "object" && !Array.isArray(value)) {
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          flatParams[nestedKey] = nestedValue;
        }
      } else {
        flatParams[key] = value;
      }
    }

    const queryString = Object.entries(flatParams)
      .map(([key, value]) => {
        // Skip encoding for email
        if (key === "email") {
          return `${key}=${value}`;
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join("&");

    const fullUrl = `${baseUrl}?${queryString}`;
    return fullUrl;
  };

  const localUser = user || JSON.parse(localStorage.getItem("userinfo")) || {};
  const dayRemaining = localUser?.dbData?.dayRemaining ?? 1;
  if (dayRemaining <= 0) {
    return (
      <Subscription
        pageName="aistudio"
        isExpire={true}
        role={localUser?.role || ""}
      />
    );
  }

  return (
    <Flex column style={{ height: "calc(-60px + 100vh)" }}>
      <Flex style={{ flexGrow: 1, position: "relative" }}>
        {isLoading && (
          <Flex
            fill
            hAlign="center"
            vAlign="center"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: "white",
            }}
          >
            <Loader size="medium" />
          </Flex>
        )}
        <iframe
          key={iframeKey} // Add key to force remount on refresh
          src={getIframeUrl()}
          className={currentConfig.className}
          style={{ width: "100%", borderTop: "none", height: "100%" }}
          allow="fullscreen; camera; microphone; clipboard-read; clipboard-write; display-capture"
          id="aiStudio"
          title={`AI ${type.charAt(0).toUpperCase() + type.slice(1)}`}
          onLoad={() => {
            postCrossDomainMessage();
            setIsLoading(false);
          }}
          onError={(error) => {
            console.error("Error during authentication", error);
            setIsLoading(false); // Ensure loading state is cleared even on error
          }}
        />
      </Flex>
    </Flex>
  );
};

export default AiStudio;
